import React from "react";
import { Descriptions, Tag, Typography } from "antd";

import { BundleInterface } from "../../../../store/mine_craft/bundlesSlice";
import Card from "../../../../componets/Card";

interface propsInterface {
  bundle: BundleInterface | undefined;
}

const FALLBACK = <Typography.Text type='secondary'>—</Typography.Text>;

// We keep the full PEM out of the rendered DOM so a screen-share or
// console log won't expose the key. Operators can still confirm a
// non-empty value via the "configured" tag and the line count.
function maskPrivateKey(pem: string | null) {
  if (!pem) return FALLBACK;

  const lines = pem.split('\n').filter(Boolean).length;
  return (
    <span>
      <Tag color='green'>configured</Tag>
      <Typography.Text type='secondary' style={{ fontSize: 12 }}>
        {lines}-line PEM block
      </Typography.Text>
    </span>
  );
}

// The App Store Connect `.p8` key file is uploaded — we only get a
// remote URL back, never the bytes. Show a "configured" pill with a
// link so the operator can confirm the upload landed without
// exposing the contents inline.
function renderPrivateKeyUrl(url: string | undefined) {
  if (!url) return FALLBACK;

  return (
    <span>
      <Tag color='green'>configured</Tag>
      <Typography.Link href={url} target='_blank' rel='noreferrer' style={{ fontSize: 12 }}>
        download
      </Typography.Link>
    </span>
  );
}

const _Details = ({ bundle }: propsInterface) => {
  if (!bundle) {
    return null;
  }

  const platformLabel = bundle.platform ? bundle.platform.toUpperCase() : '—';
  const isIos = bundle.platform === 'ios';

  return (
    <>
      <Card className='v-mb-20'>
        <Descriptions
          title='Attestation'
          column={1}
          size='small'
          bordered
          labelStyle={{ width: 220 }}
        >
          <Descriptions.Item label='Platform'>
            <Tag>{platformLabel}</Tag>
          </Descriptions.Item>

          <Descriptions.Item label='Apple Team ID'>
            {bundle.apple_team_id ? (
              <Typography.Text code copyable>{bundle.apple_team_id}</Typography.Text>
            ) : FALLBACK}
          </Descriptions.Item>

          <Descriptions.Item label='Firebase Project ID'>
            {bundle.firebase_project_id ? (
              <Typography.Text code copyable>{bundle.firebase_project_id}</Typography.Text>
            ) : FALLBACK}
          </Descriptions.Item>

          <Descriptions.Item label='Firebase Client Email'>
            {bundle.firebase_client_email ? (
              <Typography.Text code copyable>{bundle.firebase_client_email}</Typography.Text>
            ) : FALLBACK}
          </Descriptions.Item>

          <Descriptions.Item label='Firebase Private Key'>
            {maskPrivateKey(bundle.firebase_private_key)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {isIos && (
        <Card className='v-mb-20'>
          <Descriptions
            title='In-App Purchases (StoreKit2)'
            column={1}
            size='small'
            bordered
            labelStyle={{ width: 220 }}
          >
            <Descriptions.Item label='API Key ID'>
              {bundle.app_store_connect_api_key_id ? (
                <Typography.Text code copyable>{bundle.app_store_connect_api_key_id}</Typography.Text>
              ) : FALLBACK}
            </Descriptions.Item>

            <Descriptions.Item label='API Issuer ID'>
              {bundle.app_store_connect_api_issuer_id ? (
                <Typography.Text code copyable>{bundle.app_store_connect_api_issuer_id}</Typography.Text>
              ) : FALLBACK}
            </Descriptions.Item>

            <Descriptions.Item label='API Private Key (.p8)'>
              {renderPrivateKeyUrl(bundle.app_store_connect_api_private_key_url)}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}
    </>
  );
}

export default _Details;
